import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col, Card, Alert } from 'react-bootstrap';
import logo from '../../logo.png';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { BASE_URL, extractErrorMessage } from '../../constants/common';
import { toast, ToastContainer } from "react-hot-toast";

export default function ResetPassword() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.username || '');
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [validated, setValidated] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  // Real-time validation
  useEffect(() => {
    const newErrors = {};

    // Username validation
    if (touched.username) {
      if (!email.trim()) {
        newErrors.username = 'Username is required';
      } else if (email.trim().length < 3) {
        newErrors.username = 'Username must be at least 3 characters long';
      }
    }

    setErrors(newErrors);
  }, [email, touched]);

  const resetPasswordRequest = async (email) => {
    return await axios.post(`${BASE_URL}api/v1/user/reset/password`, {
      username: email,
    });
  };

  const handleChange = (e) => {
    const { value } = e.target;
    setEmail(value);

    // Clear errors on change
    if (errors.username) {
      setErrors({});
    }
  };

  const handleBlur = (e) => {
    setTouched(prev => ({ ...prev, username: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Mark field as touched for validation display
    setTouched({ username: true });
    setValidated(true);

    // Client-side validation
    const formErrors = {};

    if (!email.trim()) {
      formErrors.username = 'Username is required';
    } else if (email.trim().length < 3) {
      formErrors.username = 'Username must be at least 3 characters long';
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    try {
      const response = await resetPasswordRequest(email);

      if (response.data?.type === 'success') {
        toast.success(response.data.message || 'Reset link sent to your email.');
        setSuccessMsg(response.data.message || 'Reset link sent to your email.');
        setEmail('');
        setTouched({});
        setValidated(false);
        setErrors({});
      } else {
        const message = response.data?.message || 'Something went wrong';
        setErrors({ username: message });
        toast.error(message);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to send reset link. Please try again later.';
      setErrors({ username: errorMessage });
      toast.error(extractErrorMessage(error));
    }
  };

  return (
    <section className="login-content">
      <Container fluid className="h-100">
        <Row className="justify-content-center align-items-center height-self-center">
          <Col md={5} sm={12} className="align-self-center">
            <Card className="sign-user_card">
              <Card.Body>
                <div className="text-center mb-4">
                  <img src={logo} className="img-fluid rounded-normal light-logo logo" alt="logo" />
                </div>

                <h3 className="mb-3 text-center">Reset Password</h3>
                <p className="text-center mb-4">Enter your username to receive a reset link.</p>

                {successMsg && (
                  <Alert
                    variant="success"
                    className="mb-3"
                    dismissible
                    onClose={() => setSuccessMsg(null)} // clear message on close
                  >
                    {successMsg}
                  </Alert>
                )}

                <Form noValidate validated={validated} onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      name="username"
                      placeholder="Enter your username"
                      value={email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.username && errors.username}
                      isValid={touched.username && !errors.username && email.trim().length >= 3}
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.username}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Link to="/login" className="text-primary">
                      Back to Login
                    </Link>
                  </div>

                  <div className="d-flex justify-content-center">
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="fw-bold"
                      disabled={Object.keys(errors).length > 0 || !email.trim() || email.trim().length < 3}
                    >
                      Send Reset Link
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
}